// Adapters: shape platform catalog rows (CatalogEntry, from the server's
// /me/inventory/catalogs/:kind endpoint) into the local types the
// SidebarPanel primitives consume.

import type { CatalogEntry } from "@/services/inventory_types";
import type {
  ComboOption,
  TagFlavorFamily,
  TagLocation,
  TypeCatalogRow,
} from "./_types";

/** TYPE-kind rows (currency / unit / storage-condition / form / standard / claim).
 *  The server returns rows with `slug` (machine code, e.g. "aud"), `display_text`
 *  ("AUD"), and `description` ("Australian Dollar"); CatalogEntry leaves `code`
 *  unpopulated for these kinds. */
export function adaptTypeRow(e: CatalogEntry): TypeCatalogRow {
  return {
    id: e.id,
    code: e.code ?? e.displayText ?? e.slug ?? "",
    label: e.displayText ?? e.code ?? "",
    ...(e.description ? { description: e.description } : {}),
    ...(e.symbol ? { symbol: e.symbol } : {}),
    ...(e.category ? { category: e.category } : {}),
    ...(e.state ? { state: e.state } : {}),
    // claim kind: surface region display_texts as `scopes` so chips and
    // dropdown rows render them via the existing scope rendering path.
    ...(e.regions && e.regions.length > 0
      ? { scopes: e.regions.map((r) => r.displayText) }
      : {}),
  };
}

export function adaptTypeRows(items: CatalogEntry[] | undefined): TypeCatalogRow[] {
  return (items ?? []).map(adaptTypeRow);
}

/** Flavor family — TAG kind. Color per-org; isDefault when no org scope. */
export function adaptFlavorFamily(e: CatalogEntry): TagFlavorFamily {
  return {
    id: e.id,
    label: e.displayText ?? e.name ?? e.slug ?? "",
    color: e.color ?? "#9aa39a",
    isDefault: !e.scopeOrganizationId,
  };
}

export function adaptFlavorFamilies(
  items: CatalogEntry[] | undefined,
): TagFlavorFamily[] {
  return (items ?? []).map(adaptFlavorFamily);
}

/** Location — TAG kind. */
export function adaptLocation(e: CatalogEntry): TagLocation {
  return {
    id: e.id,
    label: e.displayText ?? e.name ?? e.slug ?? "",
    storage_condition_id: e.storageConditionId ?? null,
    actual_storage_condition_id: e.actualStorageConditionId ?? null,
    actual_storage_condition_raw: e.actualStorageConditionRaw ?? null,
  };
}

export function adaptLocations(items: CatalogEntry[] | undefined): TagLocation[] {
  return (items ?? []).map(adaptLocation);
}

/** Custom-tag suggestion pool — flat strings. */
export function adaptCustomSuggestions(
  items: CatalogEntry[] | undefined,
): string[] {
  return (items ?? []).map((e) => e.displayText ?? e.name ?? "").filter(Boolean);
}

/** Generic flat ComboOption (Code / Catalog no.). */
export function adaptComboOption(e: CatalogEntry): ComboOption {
  return {
    value: e.displayText ?? e.code ?? e.slug ?? "",
    label: e.displayText ?? e.code ?? e.slug ?? "",
    ...(e.description ? { hint: e.description } : {}),
  };
}
